
import React from "react";
import { FormControls, i18nManager } from "../../../../../i18n";
import { CSSTransition } from 'react-transition-group';
import { ApiResponseData, BaseService, FaqService, ICountry, ICurrency, ISupportRequest, IPlatform, SanitizedResponse, SupportRequestService, ISupportRequestParticipant, SupportRequestStatus, SupportCategoryService, ISupportCategory, IProfile } from "../../../../../services";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, Transition, Table, DataManageRef, Column, Checkbox, NoseurObject, AlertInterface } from "@rivtn/noseur";

export interface RequestsProps {
    profile: IProfile;
    platform: IPlatform;
}

function Requests(props: RequestsProps) {
    const navigate = useNavigate();
    const { platform, profile } = props;
    const transitionNodeRef = React.useRef<any>(null);
    const tableManageRef = React.useRef<DataManageRef>(null);
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [showFilters, setShowFilters] = React.useState<boolean>();
    const supportRequestService = SupportRequestService.getInstance();
    const supportCategoryService = SupportCategoryService.getInstance();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.ticket_requests;
    const [supportRequests, setSupportRequests] = React.useState<ApiResponseData<ISupportRequest>>({});

    React.useEffect(fetchRequests, [searchParams]);

    return (<div className="charon-messages">
        <div className="management">
            <div className="controls">
                <div>
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.create_a_ticket} raised onClick={createTicket} />
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filter} leftIcon="fa fa-filter" raised outlined={!showFilters}
                        onClick={() => setShowFilters(!showFilters)} />
                </div>
            </div>
            <CSSTransition classNames={Transition.CURTAIN} timeout={500} in={showFilters} nodeRef={transitionNodeRef} unmountOnExit>
                <div className="filters" ref={transitionNodeRef}>
                    {labels.filters.form?.map((control: FormControls, index: number) => {
                        return (<div key={index} className="filter">
                            <span>{control.title}</span>
                            {Elements.buildConfirmDialogForm([control], (name, value) => {

                            }, searchParams, undefined, { marginTop: 0 }, undefined, { marginTop: 7 }, setSearchParams, undefined, undefined, { platform_id: "messages_platform_id" })}
                        </div>);
                    })}
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filters.reset_filter} outlined onClick={() => {
                        Utils.updateSearchParamses(labels.filters.form.reduce((acc, control: FormControls) => {
                            acc[(control.name === "platform_id" ? "platform.platform_id" : control.name)] = undefined;
                            return acc;
                        }, {} as any), setSearchParams);
                    }} />
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filters.apply} style={{ padding: "10px 40px" }} onClick={fetchRequests} />
                </div>
            </CSSTransition>
            <Table className="table" data={supportRequests.content} rowsPerPage={supportRequests.size ?? 10} totalRecords={supportRequests.total_elements} dataRefreshKeys={["external_id"]}
                manageRef={tableManageRef} loadingState={Elements.LOADING_STATE} onRowSelection={(value: ISupportRequest) => {
                    navigate({
                        search: "platform_id=" + platform.platform_id,
                        pathname: `/dashboard/management/ticket-requests/requests/${value.external_id}`,
                    });
                    return true;
                }} emptyState={<div className="es" style={{ marginTop: 20 }}>
                    <i className="fa fa-bullseye" />
                    <span style={{ maxWidth: 150 }}>{i18nManager.Labels.common.no} {labels.fine_name} {i18nManager.Labels.common.found.toLowerCase()} <span onClick={createTicket}>{labels.create}</span> {i18nManager.Labels.common.a_new} {labels.fine_name}</span>
                </div>} rowProps={{ className: "audit" }} valuedRowProps={(data?: ISupportRequest) => {
                    if (data?.status === SupportRequestStatus.STALLED) return { className: "sr-stalled" };
                    return {};
                }}>
                <Column header={() => <Checkbox disabled scheme={Schemes.RIVTN_QUIRINUS} />} template={(_) => <Checkbox scheme={Schemes.RIVTN_QUIRINUS} />} />
                <Column header={labels.table.ticket_id} dataKey="request_id" />
                <Column header={labels.table.date_created} dataKey="created_at" template={Utils.formatToFineDate3} />
                <Column header={labels.table.title} dataKey="title" />
                <Column header={labels.table.requester} dataKey="user" template={(user: ISupportRequestParticipant) => (`${user.first_name} ${user.last_name ?? ""} (${user.email})`)} />
                <Column header={labels.table.status} dataKey="status" template={(status: SupportRequestStatus) => selectStyledStatus(status)} />
            </Table>
            {Elements.buildPaginator(supportRequests ?? {}, ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, id: null, size }, setSearchParams, fetchRequests), parseInt(searchParams.get("page") ?? "1"))}
        </div>
    </div>);

    function fetchRequests() {
        setSupportRequests({});
        supportRequestService.querySupportRequests(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ISupportRequest>) => {
            setSupportRequests(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => {
            tableManageRef.current?.setLoadingState(false);
        });
    }

    function createTicket() {
        const payload: NoseurObject<any> = {};
        const dialogLabels = labels.dialogs.create;
        const categories: { label: string; value: string; }[] = [];

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.create,
            style: { minWidth: 450, maxWidth: "80%" },
            onLoading: async (alert: AlertInterface) => {
                supportCategoryService.queryCategories({ "platform.platform_id": platform?.platform_id }).then(({ apiResponse }: SanitizedResponse<ISupportCategory>) => {
                    apiResponse.data?.content?.forEach((c) => categories.push(({ label: c.name, value: c.external_id })));
                    alert.doneLoading();
                }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                    alert.doneLoading(() => {
                        dialogErrorRef.current!.innerText = errorMessage;
                    });
                });
                return false;
            },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: any, searching: any) => {
                    dialogErrorRef.current!.innerText = " ";
                    payload[name] = value;
                    return false;
                }, payload, undefined, { marginTop: 0 }, {
                    "category_id": categories
                })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (!payload.category_id || !payload.message) {
                    dialogErrorRef.current!.innerText = dialogLabels.fields_required;
                    return false;
                }
                button?.setLoadingState(true);
                payload.platform_id = platform?.platform_id;
                payload.user = {
                    middle_name: "",
                    email: profile.email,
                    last_name: profile.last_name,
                    first_name: profile.first_name,
                    profile_picture_location: profile.profile_picture_location
                };
                supportRequestService.createSupportRequest(payload).then(({ }: SanitizedResponse<ISupportRequest>) => {
                    Siren.alert(dialogLabels.successful);
                    dialog.destroy(() => setTimeout(() => fetchRequests(), 2000)); // TODO redirect to the ticket page
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function selectStyledStatus(status: SupportRequestStatus) {
        if (status === SupportRequestStatus.CLOSED) return (<span className="pill" style={{ "--backgroundColor": "#9461BC" } as any}>{labels.table.statuses.closed}</span>);
        else if (status === SupportRequestStatus.STALLED) return (<span className="pill" style={{ "--backgroundColor": "#C7A859" } as any}>{labels.table.statuses.stalled}</span>);
        else if (status === SupportRequestStatus.RESOLVED) return (<span className="pill" style={{ "--backgroundColor": "#0FA883" } as any}>{labels.table.statuses.resolved}</span>);
        else if (status === SupportRequestStatus.RE_OPENED) return (<span className="pill" style={{ "--backgroundColor": "#A5A80F" } as any}>{labels.table.statuses.re_opened}</span>);
        else if (status === SupportRequestStatus.UNRESOLVED) return (<span className="pill" style={{ "--backgroundColor": "#FF7171" } as any}>{labels.table.statuses.resolved}</span>);
        return (<span className="pill" style={{ "--backgroundColor": "#A5A80F" } as any}>{labels.table.statuses.opened}</span>);
    }

}

export default Requests;
